import { format } from 'date-fns';
import { shapeDate } from '@avangard/utils/data';
import {
    BaseEntity,
    Entity as DecribeEntity,
    Relation,
    RelationList,
} from '@avangard/utils/entity';

import { i18n } from '@lib/i18n';
import { FileInfoEntity, RegionEntity, LocationEntity } from '@modules/shared/entities';
import { UserEntity } from '@modules/user/entities';
import { LapAccessEntityList } from '@modules/lap/entities';
import { SchoolEntity } from '@modules/schools/entities';
import {
    performanceManagmentTranslates,
    creditOfPointTranslates,
    interestTranslates,
} from '@modules/students/constants';
import { SocialsEntity } from './socials-entity';
import { BuildingEntity } from './building.entity';
import { BuildingRoomEntity } from './building-room.entity';
import { StudentMedicalEntity } from './student-medical.entity';
import { StudentIndividualEntity } from './student-individual.entity';
import { StudentParentEntityList } from './student-parent.entity-list';
import { IntegrationEntity } from './integration-entity';
import { StudentOnlineApplicationEntity } from './student-online-application.entity';

import type { UserEntityType } from '@modules/user/entities';
import type { LapAccessEntity } from '@modules/lap/entities';
import type { SchoolEntityType } from '@modules/schools/entities';
import {
    Student,
    Gender,
    PerformanceManagement,
    CreditOfPoint,
    StudentAdditionalNote,
} from '@modules/types/graphql';
import type { Entity as SocialsEntityType } from './socials-entity';
import type { StudentParentEntity } from './student-parent.entity';

export type Entity =
    | (Partial<Omit<Student, 'school' | 'attachedUser'>> & {
          school?: SchoolEntityType;
          attachedUser?: UserEntityType;
          socials?: SocialsEntityType;
      })
    | null;

@DecribeEntity('StudentEntity')
class StudentEntity extends BaseEntity {
    id: string;
    fullName: string;
    nickname: string;
    birthdate: Date | null;
    passportSerial: string;
    passportNumber: string;
    passportIssued: string;
    passportAddress: string;
    address: string;
    roomNumber: string;
    receptionDate: Date | null;
    departureDate: Date | null;
    phone: string;
    email: string;
    company: number;
    platoon: number;
    platoonX: number;
    platoonId: string;
    badgeChipId: string;
    gender: Gender | string;
    accessSentAt: string;
    isAccessSent: boolean;
    accessSentStatus: string;
    accessSentStatusText: string;
    isOnline: boolean;
    isCommercial: boolean;
    isOut: boolean;
    isCertificate: boolean;
    performanceManagement: (PerformanceManagement | null)[];
    creditOfPoints: (CreditOfPoint | null)[];
    interest: (string | null)[];
    schoolClass: string;
    schoolClassProfile: string;
    facultative: string;
    clubs: string;
    sportSection: string;
    sportsCategory: string;
    qrCodeUrl: string;
    photoId: string;
    additionalNote: StudentAdditionalNote;

    @Relation(() => FileInfoEntity)
    photo: FileInfoEntity;

    @Relation(() => RegionEntity)
    region: RegionEntity;

    @Relation(() => LocationEntity)
    location: LocationEntity;

    @Relation(() => SchoolEntity)
    school: SchoolEntity;

    @Relation(() => UserEntity)
    attachedUser: UserEntity;

    @Relation(() => StudentMedicalEntity)
    medical: StudentMedicalEntity;

    @Relation(() => StudentIndividualEntity)
    individual: StudentIndividualEntity;

    @Relation(() => SocialsEntity)
    socials: SocialsEntity;

    @RelationList(() => StudentParentEntityList)
    parents: StudentParentEntity[];

    @RelationList(() => LapAccessEntityList)
    lapAccessList: (LapAccessEntity | null)[];

    @RelationList(() => LapAccessEntityList)
    schoolLapAccessList: (LapAccessEntity | null)[];

    @Relation(() => BuildingEntity)
    building: BuildingEntity;

    @Relation(() => BuildingRoomEntity)
    room: BuildingRoomEntity;

    @Relation(() => IntegrationEntity)
    integration: IntegrationEntity;

    @Relation(() => StudentOnlineApplicationEntity)
    onlineApplication: StudentOnlineApplicationEntity;

    constructor(student: Entity) {
        super(student);

        this.id = student?.id ?? '';
        this.fullName = student?.fullName ?? '';
        this.nickname = student?.nickname ?? '';
        this.address = student?.address ?? '';
        this.roomNumber = student?.roomNumber ?? '';
        this.passportSerial = student?.passportSerial ?? '';
        this.passportNumber = student?.passportNumber ?? '';
        this.passportIssued = student?.passportIssued ?? '';
        this.passportAddress = student?.passportAddress ?? '';
        this.additionalNote = student?.additionalNote ?? StudentAdditionalNote.empty;

        this.birthdate = shapeDate(student?.birthdate);
        this.receptionDate = shapeDate(student?.receptionDate);
        this.departureDate = shapeDate(student?.departureDate);

        this.photoId = student?.photoId ?? '';
        this.phone = student?.phone ?? '';
        this.email = student?.email ?? '';
        this.company = student?.company ?? 0;
        this.platoon = student?.platoon ?? 0;
        this.platoonX = student?.platoonX ?? 0;
        this.platoonId = student?.platoonId ?? '';
        this.badgeChipId = student?.badgeChipId ?? '';
        this.gender = student?.gender ?? '';
        this.accessSentAt = student?.accessSentAt ?? '';
        this.isAccessSent = student?.isAccessSent ?? false;
        this.accessSentStatus = student?.accessSentStatus ?? '';
        this.accessSentStatusText = student?.accessSentStatusText ?? '';
        this.isOnline = student?.isOnline ?? false;
        this.isCommercial = student?.isCommercial ?? false;
        this.isOut = student?.isOut ?? false;
        this.isCertificate = student?.isCertificate ?? false;
        this.performanceManagement = student?.performanceManagement ?? [];
        this.creditOfPoints = student?.creditOfPoints ?? [];
        this.interest = student?.interest ?? [];
        this.schoolClass = student?.schoolClass ?? '';
        this.schoolClassProfile = student?.schoolClassProfile ?? '';
        this.facultative = student?.facultative ?? '';
        this.clubs = student?.clubs ?? '';
        this.sportSection = student?.sportSection ?? '';
        this.sportsCategory = student?.sportsCategory ?? '';
        this.qrCodeUrl = student?.qrCodeUrl ?? '';
    }

    isChosenAdditionalNote() {
        return this.additionalNote !== StudentAdditionalNote.empty;
    }

    preparePerfomanceManagmentForForm() {
        const result = this.prepareEnumForForm(
            this.performanceManagement,
            performanceManagmentTranslates,
        );

        return result;
    }

    prepareCreditOfPointForForm() {
        const result = this.prepareEnumForForm(this.creditOfPoints, creditOfPointTranslates);

        return result;
    }

    prepareInterestForForm() {
        const result = this.prepareEnumForForm(this.interest, interestTranslates);

        return result;
    }

    prepareEnumForForm(array: any[], translates: Record<string, any>): AutocompleteOption<any>[] {
        const hasValues = array.filter(Boolean).length !== 0;

        if (!hasValues) {
            return [];
        }

        const result = array.map(value => ({
            key: value,
            value: i18n.t(translates[value]),
        }));

        return result;
    }

    getFormattedAccessSentDate() {
        if (this.accessSentAt === null) {
            return '-';
        }

        try {
            const validDate = new Date(this.accessSentAt);
            const date = format(validDate, 'dd.MM.yyyy');
            const time = format(validDate, 'HH:mm:ss');

            const formatted = i18n.t('common:blocks.time.date_to_minutes', { date, time });

            return formatted;
        } catch {
            return '-';
        }
    }

    getFormattedBirthdate() {
        if (!this.birthdate) {
            return '-';
        }

        try {
            const date = format(new Date(this.birthdate), 'dd.MM.yyy');

            return date;
        } catch {
            return '-';
        }
    }

    getFormattedCompany() {
        if (!this.company) {
            return '-';
        }

        return this.company;
    }

    getFormattedPlatoon(platoon: number) {
        if (!platoon) {
            return '-';
        }

        return platoon;
    }

    getUniqueLapAccessList() {
        const uniqueLapAccessList = this.schoolLapAccessList
            ?.filter(
                schoolLapAccess =>
                    !this.lapAccessList?.find(
                        lapAccess => lapAccess?.lap?.id === schoolLapAccess?.lap?.id,
                    ),
            )
            ?.map(lapAccess => lapAccess?.lap ?? null);

        return uniqueLapAccessList ?? [];
    }

    getLapsFromLapAccessList() {
        const laps = (this.lapAccessList.filter(Boolean) as LapAccessEntity[]).map(
            lapAccess => lapAccess.lap,
        );

        return laps;
    }
}

export { StudentEntity };
